import clsx from "clsx";
import type { TypeOptions as TypeToast } from "react-toastify";
import { ToastContainer, ToastContentProps, ToastOptions, toast } from "react-toastify";
import { DefaultComponentProps } from "@/interfaces/common";
import "react-toastify/dist/ReactToastify.css";
import "../../app/globals.css";
import { Icon, IconName } from "@/components/common";

const TOAST_CONTAINER_CLASS_NAME = "rounded-md bg-white !p-4 shadow-box-primary";

type IconProps = Readonly<{
  type: TypeToast;
  icon: IconName;
}>;

type ToastProps = {
  type: Extract<TypeToast, "success" | "error" | "info" | "default">;
  title: string;
  content?: string;
} & ToastOptions;

type IconOptions = Extract<TypeToast, "success" | "error" | "info" | "default">;

const icons: Record<IconOptions, React.ReactElement | null> = {
  success: <ToastIcon type="success" icon="Success" />,
  error: <ToastIcon type="error" icon="Cancel" />,
  info: <ToastIcon type="info" icon="Info" />,
  default: null,
};

export const ToastProvider = ({ children }: DefaultComponentProps) => {
  return (
    <>
      {children}
      <ToastContainer
        limit={1}
        position="bottom-right"
        icon={false}
        closeButton={false}
        hideProgressBar
        className="w-[329px]"
        toastClassName={TOAST_CONTAINER_CLASS_NAME}
        bodyClassName="m-0 p-0 text-body font-gotham"
      />
    </>
  );
};

function ToastIcon({ type, icon }: IconProps) {
  return (
    <Icon
      name={icon}
      size="1.5rem"
      className={clsx({
        "text-error": type === "error",
        "text-success": type === "success",
        "text-info": type === "info",
      })}
    />
  );
}

export function showToast({ type, title, content, ...options }: ToastProps) {
  const { data: _ignored, ...restOptions } = options;

  toast.dismiss();

  toast(CustomNotification, {
    data: { type, title, content },
    autoClose: 3000,
    hideProgressBar: true,
    position: "bottom-right",
    className: TOAST_CONTAINER_CLASS_NAME,
    ...restOptions,
  });
}

function CustomNotification({ data, closeToast }: Readonly<ToastContentProps<ToastProps>>) {
  const { type, title, content } = data;
  return (
    <div className="flex gap-2">
      {icons[type as IconOptions]}

      <Icon
        name="Close"
        className={clsx("absolute right-2 top-3", {
          "top-5": type === "default" || content === undefined,
        })}
        onClick={closeToast}
      />

      <div className="mr-2 flex flex-1 flex-col font-gotham text-neutral-text">
        <span className="text-sm font-medium">{title}</span>

        {content && <span className="text-xs font-normal">{data.content}</span>}
      </div>
    </div>
  );
}
